/**
 * Mux Data (web) — tracking unificado para admin analytics.
 *
 * Mux Video webhooks NO reportan plays/watch time. El engagement se mide con
 * Mux Data vía envKey en @mux/mux-player-react + metadata por view.
 *
 * Admin: /admin/mux-video-analytics (Mux Data API pull) + PostHog video_interaction.
 *
 * Env:
 *   VITE_MUX_ENV_KEY — Environment key del dashboard Mux Data (pública, frontend OK)
 *
 * Docs: https://www.mux.com/docs/guides/monitor-mux-player
 */

import { BRAND_SOURCE_KEY } from "@/lib/brandConfig";

/** Coincide con EXTERNAL_ID en api/admin/mux-video-analytics.ts (filtro brand). */
export const MUX_DATA_EXTERNAL_ID =
  (typeof import.meta !== "undefined" &&
    (import.meta as { env?: Record<string, string> }).env?.VITE_MUX_EXTERNAL_ID?.trim()) ||
  "juap";

export const MUX_DATA_CREATOR_ID = BRAND_SOURCE_KEY;

/**
 * Env key Mux Data. Sin esto el player reproduce pero no reporta views/watch time.
 */
export function getMuxDataEnvKey(): string | undefined {
  try {
    const fromVite = import.meta.env?.VITE_MUX_ENV_KEY;
    if (typeof fromVite === "string" && fromVite.trim()) return fromVite.trim();
  } catch {
    // Node / tests
  }
  return undefined;
}

export type MuxDataSurface =
  | "mux_cta"
  | "mux_player"
  | "reels"
  | "us_exclusive"
  | "dual_cta"
  | "premium"
  | "channel_invite"
  | "gallery"
  | "other";

export interface MuxDataMetadataInput {
  playbackId: string;
  title?: string | null;
  /** Superficie de producto para desglose en Mux dashboard. */
  surface?: MuxDataSurface | string;
  country?: string | null;
  /** Ruta SPA (pathname). */
  pagePath?: string | null;
  /** Template / host (USExclusiveLanding, DualCTA, …). */
  template?: string | null;
  /** Variant dual o reel index. */
  variantId?: string | null;
  viewerUserId?: string | null;
}

/**
 * Metadata canónica para MuxPlayer `metadata={...}`.
 * custom_1 = external brand id (juap) — alineado al filtro del admin.
 * custom_2 = surface
 * custom_3 = country
 * custom_4 = page path
 * custom_5 = template / variant
 */
export function buildMuxDataMetadata(
  input: MuxDataMetadataInput,
): Record<string, string> {
  const playbackId = (input.playbackId || "").trim();
  const title = (input.title || "").trim() || playbackId || "video";
  const surface = (input.surface || "other").trim() || "other";
  const country = (input.country || "").trim().toUpperCase().slice(0, 2);
  const pagePath =
    (input.pagePath ||
      (typeof window !== "undefined" ? window.location.pathname : "") ||
      "")
      .trim()
      .slice(0, 120) || "/";
  const template = (input.template || "").trim().slice(0, 80);
  const variantId = (input.variantId || "").trim().slice(0, 80);

  const meta: Record<string, string> = {
    video_id: playbackId,
    video_title: title,
    video_stream_type: "on-demand",
    video_series: MUX_DATA_CREATOR_ID,
    video_producer: MUX_DATA_CREATOR_ID,
    // Brand filter used by admin + Mux dashboard custom dimensions
    custom_1: MUX_DATA_EXTERNAL_ID,
    custom_2: surface,
    custom_3: country || "XX",
    custom_4: pagePath,
    custom_5: [template, variantId].filter(Boolean).join(":") || surface,
  };

  if (input.viewerUserId?.trim()) {
    meta.viewer_user_id = input.viewerUserId.trim();
  }

  return meta;
}

/** True si el player reportará a Mux Data (env key presente). */
export function isMuxDataTrackingEnabled(): boolean {
  return Boolean(getMuxDataEnvKey());
}
